<template>
    <div class="mailing-builder">
        <div class="row mt-3">
            <div class="col-md-4">
                <label class="form-label">Nombre del mailing</label>
                <input type="text" v-model="mailingName" class="form-control" placeholder="Nombre del mailing">
            </div>
            <div class="col-md-4">
                <label class="form-label">Asunto del mailing</label>
                <input type="text" v-model="mailingSubject" class="form-control" placeholder="Asunto del mailing">
            </div>
            <div class="col-md-4 d-flex align-items-end">
                <button v-if="!mailingFileName" class="btn btn-primary" :disabled="creating" @click="createMailing">
                    {{ creating ? 'Creando...' : 'Crear' }}
                </button>
                <button v-else class="btn btn-outline-secondary ms-2"
                    @click="update" title="Actualizar" aria-label="Actualizar">
                    <span class="builder-icon"><i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i></span>
                </button>
                <button v-if="mailingFileName" class="btn btn-outline-secondary ms-2"
                    @click="openTestModal" title="Enviar prueba" aria-label="Enviar prueba">
                    <span class="builder-icon"><i class="fa-solid fa-paper-plane" aria-hidden="true"></i></span>
                </button>
                <button v-if="mailingFileName" class="btn btn-outline-primary ms-2"
                    @click="openPreview" title="Ver mailing" aria-label="Ver mailing">
                    <span class="builder-icon"><i class="fa-regular fa-eye" aria-hidden="true"></i></span>
                </button>
                <button v-if="mailingFileName" class="btn btn-outline-danger ms-2"
                    @click="deleteMailing" title="Eliminar" aria-label="Eliminar">
                    <span class="builder-icon"><i class="fa-regular fa-trash-can" aria-hidden="true"></i></span>
                </button>
            </div>
        </div>
        <div v-if="mailingFileName" class="row mt-3">
            <div class="col-md-4">
                <label class="form-label">Destinatarios</label>
                <select v-model="campaignAudience" class="form-control">
                    <option value="all">Usuarios y negocios</option>
                    <option value="users">Solo usuarios registrados</option>
                    <option value="businesses">Solo negocios</option>
                </select>
            </div>
            <div class="col-md-8 d-flex align-items-end">
                <button class="btn btn-success" type="button" :disabled="sendingCampaign" @click="sendCampaign">
                    {{ sendingCampaign ? 'Enviando campaña...' : 'Enviar campaña' }}
                </button>
                <span v-if="campaignMessage" class="ms-3 text-muted">{{ campaignMessage }}</span>
            </div>
        </div>
        <div v-if="mailingFileName" class="row mt-4">
            <div class="col-md-3">
                <div class="builder-sidebar">
                    <div class="sidebar-content">
                        <h6>Opciones de construccion</h6>
                        <div class="option-grid">
                            <div class="option-card" v-for="option in options" :key="option.key"
                                draggable="true"
                                @dragstart="onDragStart(option, $event)"
                                @click="addBlock(option)">
                                <div class="option-icon">
                                    <span class="builder-icon">
                                        <i :class="iconClass(option.icon)" aria-hidden="true"></i>
                                    </span>
                                </div>
                                <div class="option-name">{{ option.label }}</div>
                            </div>
                        </div>
                        <div class="mt-4">
                            <h6>Variables disponibles</h6>
                            <p class="text-muted small mb-2">Copia y pega en el HTML o en los campos del editor.</p>
                            <div class="variable-list">
                                <template v-for="(item, index) in availableVariables" :key="item.token || item.label || index">
                                    <div v-if="item.type === 'header'" class="variable-header">
                                        {{ item.label }}
                                    </div>
                                    <button v-else
                                        class="btn btn-sm btn-outline-secondary w-100 text-start"
                                        type="button"
                                        @click="copyVariable(item.token)">
                                        {{ item.token }}
                                    </button>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md">
                <div v-if="templateSlugValue" id="constructorvisual" class="builder-canvas"
                    @dragover.prevent
                    @drop="onDrop($event)">
                    <div class="canvas-toolbar">
                        <button class="btn btn-sm btn-outline-secondary" :class="{ active: editorMode === 'visual' }"
                            @click="setEditorMode('visual')">
                            Visual
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" :class="{ active: editorMode === 'html' }"
                            @click="setEditorMode('html')">
                            HTML
                        </button>
                    </div>
                    <h6>Area de construccion</h6>
                    <div v-if="blocks.length === 0" class="text-muted">Agrega un bloque desde las opciones.</div>
                    <div v-if="editorMode === 'visual'" class="canvas-preview visual-builder"
                        @dragover.prevent="handleCanvasDragOver($event)"
                        @drop="onBlockDrop(blocks.length, $event)">
                        <div v-if="blocks.length === 0" class="text-muted">
                            Arrastra un bloque aqui para empezar.
                        </div>
                        <template v-for="(block, index) in blocks" :key="block.id">
                            <div v-if="showDropPlaceholder(index)" class="drop-placeholder">
                                <span>Soltar aqui</span>
                            </div>
                            <div class="visual-block" :data-block-id="block.id"
                                draggable="true"
                                @dragstart="onBlockDragStart(block, $event)"
                                @dragover.prevent="handleBlockDragOver(index, $event)"
                                @dragleave="handleBlockDragLeave"
                                @dragend="handleDragEnd"
                                @drop="onBlockDrop(index, $event)"
                                @contextmenu.prevent="openContextMenu(block, $event)">
                                <div class="visual-block-toolbar">
                                    <span class="visual-block-handle" title="Mover">⋮⋮</span>
                                </div>
                                <div class="visual-block-body">
                                    <table v-if="isColumnsBlock(block)" class="column-table" role="presentation" cellpadding="0" cellspacing="0">
                                        <tbody>
                                            <tr>
                                                <td class="column-cell"
                                                    v-for="(content, colIndex) in block.props.contents"
                                                    :key="colIndex"
                                                    @dragover.prevent
                                                    @drop.stop="onColumnDrop(block, colIndex, $event)">
                                                    <div class="column-drop"
                                                        :data-block-id="block.id"
                                                        :data-col-index="colIndex"
                                                        @click="onColumnClick($event)"
                                                        @contextmenu.prevent="openColumnContextMenu(block, colIndex, $event)">
                                                        <div v-if="!content" class="text-muted small">Suelta un bloque aqui</div>
                                                        <div v-else v-html="content"></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-else-if="block.key === 'raw'" class="raw-block-body" v-html="block.html"></div>
                                    <component v-else
                                        :is="block.component"
                                        :html="block.html"
                                        v-bind="block.componentProps">
                                    </component>
                                </div>
                            </div>
                        </template>
                        <div v-if="showDropPlaceholder(blocks.length)" class="drop-placeholder">
                            <span>Soltar aqui</span>
                        </div>
                    </div>
                    <div v-else class="canvas-editor">
                        <textarea v-model="htmlDraft" rows="18" class="form-control"></textarea>
                    </div>
                    <div v-if="editorMode !== 'visual'" class="canvas-blocks">
                        <div class="canvas-block" v-for="(block, index) in blocks" :key="block.id"
                            draggable="true"
                            @dragstart="onBlockDragStart(block, $event)"
                            @dragover.prevent
                            @drop="onBlockDrop(index, $event)"
                            @contextmenu.prevent="openContextMenu(block, $event)">
                            <div class="canvas-block-header">
                                <span class="small text-muted">{{ block.label }}</span>
                                <div class="d-flex gap-1">
                                    <button class="btn btn-xs btn-light" type="button" @click.stop="openBlockEditor(block)">
                                        Editar
                                    </button>
                                    <button class="btn btn-xs btn-light text-danger" type="button"
                                        @click.stop="removeBlock(block.id)">
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                            <component
                                :is="block.component"
                                class="canvas-block-body"
                                :html="block.html"
                                v-bind="block.componentProps">
                            </component>
                        </div>
                    </div>
                    <div v-if="contextMenu.visible" class="context-menu"
                        :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }">
                        <button class="context-item" @click="handleContextEdit">Editar</button>
                        <button v-if="contextMenu.mode === 'block'" class="context-item" @click="handleContextDuplicate">
                            Duplicar
                        </button>
                        <button class="context-item text-danger" @click="handleContextDelete">Eliminar</button>
                    </div>
                    <div v-if="columnMenu.visible" class="context-menu"
                        :style="{ top: columnMenu.y + 'px', left: columnMenu.x + 'px' }">
                        <button class="context-item" @click="handleColumnEdit">Editar</button>
                        <button class="context-item text-danger" @click="handleColumnDelete">Eliminar</button>
                    </div>
                </div>
                <div v-else class="builder-canvas text-muted">
                    Primero crea el mailing para habilitar el constructor.
                </div>
            </div>
        </div>
        <div v-if="testModal.visible" class="modal-mask" @click.self="closeTestModal">
            <div class="modal-panel">
                <div class="modal-header">
                    <h6 class="m-0">Enviar prueba</h6>
                    <button class="btn-close" type="button" @click="closeTestModal" aria-label="Cerrar"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-2">
                        <label class="form-label">Correo</label>
                        <input v-model="testEmail" type="email" class="form-control" placeholder="correo@ejemplo.com">
                    </div>
                    <small class="text-muted d-block">
                        Se enviara el mailing con los cambios guardados.
                    </small>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" @click="closeTestModal">Cancelar</button>
                    <button class="btn btn-primary" type="button" :disabled="sendingTest" @click="sendTestEmail">
                        {{ sendingTest ? 'Enviando...' : 'Enviar prueba' }}
                    </button>
                </div>
            </div>
        </div>
        <div v-if="imageModal.visible" class="modal-mask" @click.self="closeImageModal">
            <div class="modal-panel">
                <div class="modal-header">
                    <h6 class="m-0">Configurar imagen</h6>
                    <button class="btn-close" type="button" @click="closeImageModal" aria-label="Cerrar"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-3">
                        <label class="form-label">
                            URL de la imagen
                            <a class="ms-2 small" href="/web/marketing/archivos" target="_blank" rel="noopener">
                                (seleccionar imagen)
                            </a>
                        </label>
                        <input v-model="imageModal.src" type="text" class="form-control" placeholder="https://...">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Subir imagen (Mailing)</label>
                        <input type="file" class="form-control" accept="image/*" :disabled="imageModal.uploading"
                            @change="onImageFileChange">
                        <small class="text-muted d-block mt-1">
                            La imagen se guarda en la carpeta <strong>mailings</strong>.
                        </small>
                        <div v-if="imageModal.uploadError" class="text-danger mt-1">
                            {{ imageModal.uploadError }}
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Ancho</label>
                            <input v-model="imageModal.width" type="text" class="form-control" placeholder="100% o 600px">
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Alto</label>
                            <input v-model="imageModal.height" type="text" class="form-control" placeholder="auto o 300px">
                        </div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Enlace (opcional)</label>
                        <input v-model="imageModal.linkUrl" type="text" class="form-control" placeholder="https://...">
                    </div>
                    <div class="form-check">
                        <input v-model="imageModal.openNewTab" class="form-check-input" type="checkbox" id="imgNewTab">
                        <label class="form-check-label" for="imgNewTab">Abrir en nueva pestaña</label>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" @click="closeImageModal">Cancelar</button>
                    <button class="btn btn-primary" type="button" @click="saveImageModal">Guardar</button>
                </div>
            </div>
        </div>
        <div v-if="blockEditor.visible" class="modal-mask" @click.self="closeBlockEditor">
            <div class="modal-panel">
                <div class="modal-header">
                    <h6 class="m-0">Editar bloque</h6>
                    <button class="btn-close" type="button" @click="closeBlockEditor" aria-label="Cerrar"></button>
                </div>
                <div class="modal-body">
                    <component
                        :is="blockEditor.component"
                        v-model="blockEditor.props">
                    </component>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" @click="closeBlockEditor">Cancelar</button>
                    <button class="btn btn-primary" type="button" @click="saveBlockEditor">Guardar</button>
                </div>
            </div>
        </div>
        <div v-if="elementEditor.visible" class="modal-mask" @click.self="closeElementEditor">
            <div class="modal-panel">
                <div class="modal-header">
                    <h6 class="m-0">Editar elemento</h6>
                    <button class="btn-close" type="button" @click="closeElementEditor" aria-label="Cerrar"></button>
                </div>
                <div class="modal-body">
                    <component
                        :is="elementEditor.component"
                        v-model="elementEditor.props">
                    </component>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-outline-secondary" type="button" @click="closeElementEditor">Cancelar</button>
                    <button class="btn btn-primary" type="button" @click="saveElementEditor">Guardar</button>
                </div>
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-md-12">
                <label class="form-label">Adjuntos del correo</label>
                <input type="file" class="form-control" :disabled="attachmentUploading"
                    @change="onAttachmentChange">
                <small v-if="attachmentError" class="text-danger d-block mt-1">{{ attachmentError }}</small>
                <div v-if="mailingAttachments.length" class="mt-2 d-flex flex-wrap gap-2">
                    <div v-for="(file, index) in mailingAttachments" :key="file.path || index"
                        class="badge bg-light text-dark d-inline-flex align-items-center gap-1">
                        <span>{{ file.name }}</span>
                        <button type="button" class="btn btn-xs btn-light text-danger" @click="removeAttachment(index)">
                            <span class="builder-icon"><i class="fa-solid fa-xmark" aria-hidden="true"></i></span>
                        </button>
                    </div>
                </div>
                <small v-else class="text-muted d-block mt-2">No hay adjuntos agregados.</small>
            </div>
        </div>
    </div>
</template>

<script>
import MailingBlockColumns, { buildDefaultHtml as buildColumnsHtml } from './blocks/MailingBlockColumns.vue';
import MailingBlockImage, { buildDefaultHtml as buildImageHtml } from './blocks/MailingBlockImage.vue';
import MailingBlockText, { buildDefaultHtml as buildTextHtml } from './blocks/MailingBlockText.vue';
import MailingBlockTitle, { buildDefaultHtml as buildTitleHtml } from './blocks/MailingBlockTitle.vue';
import MailingBlockButton, { buildDefaultHtml as buildButtonHtml } from './blocks/MailingBlockButton.vue';
import MailingBlockArticle, { buildDefaultHtml as buildArticleHtml } from './blocks/MailingBlockArticle.vue';
import MailingBlockList, { buildDefaultHtml as buildListHtml } from './blocks/MailingBlockList.vue';
import MailingBlockSpacer, { buildDefaultHtml as buildSpacerHtml } from './blocks/MailingBlockSpacer.vue';
import MailingBlockSocials, { buildDefaultHtml as buildSocialsHtml } from './blocks/MailingBlockSocials.vue';
import MailingBlockRaw, { buildDefaultHtml as buildRawHtml } from './blocks/MailingBlockRaw.vue';
import MailingBlockColumnsEditor from './blocks/editors/MailingBlockColumnsEditor.vue';
import MailingBlockImageEditor from './blocks/editors/MailingBlockImageEditor.vue';
import MailingBlockTextEditor from './blocks/editors/MailingBlockTextEditor.vue';
import MailingBlockTitleEditor from './blocks/editors/MailingBlockTitleEditor.vue';
import MailingBlockButtonEditor from './blocks/editors/MailingBlockButtonEditor.vue';
import MailingBlockArticleEditor from './blocks/editors/MailingBlockArticleEditor.vue';
import MailingBlockListEditor from './blocks/editors/MailingBlockListEditor.vue';
import MailingBlockSpacerEditor from './blocks/editors/MailingBlockSpacerEditor.vue';
import MailingBlockSocialsEditor from './blocks/editors/MailingBlockSocialsEditor.vue';
import MailingBlockRawEditor from './blocks/editors/MailingBlockRawEditor.vue';

const BLOCK_OPTIONS = [
    {
        key: 'col-1',
        label: '1 Columna',
        icon: 'grid',
        component: MailingBlockColumns,
        editorComponent: MailingBlockColumnsEditor,
        componentProps: { columns: 1 },
        defaultProps: { columns: 1, padding: '8px', align: 'left', backgroundColor: '', rowHeight: '', widths: ['100%'], contents: [''] },
        buildHtml: (props) => buildColumnsHtml(props),
    },
    {
        key: 'col-2',
        label: '2 Columnas',
        icon: 'columns',
        component: MailingBlockColumns,
        editorComponent: MailingBlockColumnsEditor,
        componentProps: { columns: 2 },
        defaultProps: { columns: 2, padding: '8px', align: 'left', backgroundColor: '', rowHeight: '', widths: ['50%', '50%'], contents: ['', ''] },
        buildHtml: (props) => buildColumnsHtml(props),
    },
    {
        key: 'col-3',
        label: '3 Columnas',
        icon: 'layers',
        component: MailingBlockColumns,
        editorComponent: MailingBlockColumnsEditor,
        componentProps: { columns: 3 },
        defaultProps: { columns: 3, padding: '8px', align: 'left', backgroundColor: '', rowHeight: '', widths: ['33.33%', '33.33%', '33.33%'], contents: ['', '', ''] },
        buildHtml: (props) => buildColumnsHtml(props),
    },
    {
        key: 'image',
        label: 'Imagen',
        icon: 'image',
        component: MailingBlockImage,
        editorComponent: MailingBlockImageEditor,
        defaultProps: {
            src: '',
            alt: 'Imagen',
            width: '800px',
            height: '',
            maxWidth: '600px',
            linkUrl: '',
            openNewTab: true,
            align: 'center',
            padding: '0',
            backgroundColor: '',
        },
        buildHtml: (props) => buildImageHtml(props),
    },
    {
        key: 'text',
        label: 'Texto',
        icon: 'type',
        component: MailingBlockText,
        editorComponent: MailingBlockTextEditor,
        defaultProps: {
            content: 'Texto del mailing...',
            color: '#111827',
            fontSize: '14px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '20px',
            align: 'left',
            padding: '0',
        },
        buildHtml: (props) => buildTextHtml(props),
    },
    {
        key: 'title',
        label: 'Titulo',
        icon: 'hash',
        component: MailingBlockTitle,
        editorComponent: MailingBlockTitleEditor,
        defaultProps: {
            text: 'Titulo del mailing',
            tag: 'h2',
            color: '#111827',
            fontSize: '22px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '28px',
            align: 'left',
            padding: '0',
        },
        buildHtml: (props) => buildTitleHtml(props),
    },
    {
        key: 'button',
        label: 'Boton',
        icon: 'square',
        component: MailingBlockButton,
        editorComponent: MailingBlockButtonEditor,
        defaultProps: {
            text: 'Boton',
            url: '#',
            backgroundColor: '#0d6efd',
            textColor: '#ffffff',
            padding: '10px 16px',
            borderRadius: '4px',
            align: 'left',
            fontSize: '13px',
            fontFamily: 'Arial, sans-serif',
        },
        buildHtml: (props) => buildButtonHtml(props),
    },
    {
        key: 'article',
        label: 'Articulo',
        icon: 'file-text',
        component: MailingBlockArticle,
        editorComponent: MailingBlockArticleEditor,
        defaultProps: {
            title: 'Nota',
            content: 'Escribe aqui tu anotacion.',
            backgroundColor: '#f8fafc',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            align: 'left',
            titleColor: '#111827',
            titleSize: '16px',
            titleFamily: 'Arial, sans-serif',
            titleWeight: '600',
            titleMargin: '0 0 6px 0',
            bodyColor: '#374151',
            bodySize: '14px',
            bodyFamily: 'Arial, sans-serif',
            bodyLineHeight: '20px',
            bodyMargin: '0',
        },
        buildHtml: (props) => buildArticleHtml(props),
    },
    {
        key: 'spacer',
        label: 'Espacio',
        icon: 'minus',
        component: MailingBlockSpacer,
        editorComponent: MailingBlockSpacerEditor,
        defaultProps: {
            height: '24px',
            backgroundColor: '',
        },
        buildHtml: (props) => buildSpacerHtml(props),
    },
    {
        key: 'list',
        label: 'Lista',
        icon: 'list',
        component: MailingBlockList,
        editorComponent: MailingBlockListEditor,
        defaultProps: {
            items: ['Elemento 1', 'Elemento 2', 'Elemento 3'],
            ordered: false,
            color: '#111827',
            fontSize: '14px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '20px',
            align: 'left',
            padding: '0',
            listStyleType: 'disc',
            listStylePosition: 'outside',
        },
        buildHtml: (props) => buildListHtml(props),
    },
    {
        key: 'socials',
        label: 'Redes sociales',
        icon: 'share-2',
        component: MailingBlockSocials,
        editorComponent: MailingBlockSocialsEditor,
        defaultProps: {
            align: 'left',
            size: 28,
            gap: 8,
            items: [
                { label: 'Facebook', iconUrl: '', url: '#' },
                { label: 'Instagram', iconUrl: '', url: '#' },
                { label: 'LinkedIn', iconUrl: '', url: '#' },
            ],
        },
        buildHtml: (props) => buildSocialsHtml(props),
    },
];

const RAW_BLOCK = {
    key: 'raw',
    label: 'HTML',
    component: MailingBlockRaw,
    editorComponent: MailingBlockRawEditor,
    defaultProps: { html: '' },
    buildHtml: (props) => buildRawHtml(props),
};

const ICON_CLASS_MAP = {
    grid: 'fa-solid fa-table-cells',
    columns: 'fa-solid fa-table-columns',
    layers: 'fa-solid fa-layer-group',
    image: 'fa-regular fa-image',
    type: 'fa-solid fa-font',
    hash: 'fa-solid fa-heading',
    square: 'fa-regular fa-square',
    'file-text': 'fa-regular fa-file-lines',
    minus: 'fa-solid fa-minus',
    list: 'fa-solid fa-list-ul',
    'share-2': 'fa-solid fa-share-nodes',
};

const BLOCK_REGISTRY = BLOCK_OPTIONS.reduce((acc, option) => {
    acc[option.key] = option;
    return acc;
}, {});
BLOCK_REGISTRY.raw = RAW_BLOCK;

export default {
    name: 'MailingBuilder',
    components: {
        MailingBlockColumns,
        MailingBlockImage,
        MailingBlockText,
        MailingBlockTitle,
        MailingBlockButton,
        MailingBlockArticle,
        MailingBlockList,
        MailingBlockSpacer,
        MailingBlockSocials,
        MailingBlockRaw,
        MailingBlockColumnsEditor,
        MailingBlockImageEditor,
        MailingBlockTextEditor,
        MailingBlockTitleEditor,
        MailingBlockButtonEditor,
        MailingBlockArticleEditor,
        MailingBlockListEditor,
        MailingBlockSpacerEditor,
        MailingBlockSocialsEditor,
        MailingBlockRawEditor,
    },
    props: {
        templateSlug: { type: String, default: '' },
        apiCreateUrl: { type: String, default: '/api/mailing' },
        apiUpdateUrl: { type: String, default: '/api/mailing/update' },
        apiDeleteUrl: { type: String, default: '/api/mailing/delete' },
        apiContentUrl: { type: String, default: '/api/mailing/content' },
        apiImageUploadUrl: { type: String, default: '/api/mailing/image' },
        apiAttachmentUploadUrl: { type: String, default: '/api/mailing/attachment' },
        apiTestSendUrl: { type: String, default: '/api/mailing/test-send' },
        apiCampaignSendUrl: { type: String, default: '/api/mailing/send' },
        apiMetaUrl: { type: String, default: '/api/mailing/meta' },
        previewUrlBase: { type: String, default: '/web/marketing/mailing' },
        editUrlBase: { type: String, default: '/web/marketing/mailing' },
        indexUrl: { type: String, default: '/web/marketing/mailing' },
        apiToken: { type: String, default: '' },
    },
    mounted() {
        if (this.templateSlug) {
            this.templateSlugValue = this.templateSlug;
            this.mailingFileName = this.templateSlug;
            this.fetchMailingMeta();
            this.fetchMailingContent();
        }
        document.addEventListener('click', this.closeAllContextMenus);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.closeAllContextMenus);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    },
    watch: {
        templateSlug(newValue) {
            if (newValue) {
                this.templateSlugValue = newValue;
                this.mailingFileName = newValue;
                this.fetchMailingMeta();
                this.fetchMailingContent();
            }
        },
        htmlDraft() {
            if (this.editorMode === 'visual' && !this.htmlDraft) {
                this.clearVisualEditor();
            }
            if (this.editorMode === 'html') {
                this.scheduleBlocksSyncFromHtml();
            }
            this.updateDirtyState();
        },
        mailingName() {
            this.updateDirtyState();
        },
        mailingSubject() {
            this.updateDirtyState();
        },
        mailingAttachments: {
            deep: true,
            handler() {
                this.updateDirtyState();
            },
        },
        blocks: {
            deep: true,
            handler() {
                this.updateDirtyState();
            },
        },
    },
    data() {
        return {
            mailingName: '',
            mailingSubject: '',
            mailingAttachments: [],
            attachmentUploading: false,
            attachmentError: '',
            creating: false,
            mailingFileName: '',
            templateSlugValue: '',
            autoSaveTimer: null,
            toastTimer: null,
            htmlSyncTimer: null,
            editorMode: 'visual',
            htmlDraft: '',
            dragActive: false,
            dragOverIndex: null,
            dragLeaveTimer: null,
            history: [],
            historyIndex: -1,
            historyLocked: false,
            lastSavedSnapshot: null,
            isDirty: false,
            availableVariables: [
                { type: 'header', label: 'Usuario registrado' },
                { token: '{{ $nombre }}' },
                { token: '{{ $apellidos }}' },
                { token: '{{ $nombre_completo }}' },
                { token: '{{ $email }}' },
                { token: '{{ $telefono }}' },
                { token: '{{ $edad }}' },
                { token: '{{ $genero }}' },
                { token: '{{ $profesion }}' },
                { token: '{{ $rango_salarial }}' },

                { type: 'header', label: 'Negocio registrado' },
                { token: '{{ $nombre_negocio }}' },
                { token: '{{ $email_negocio }}' },
                { token: '{{ $telefono_negocio }}' },
                { token: '{{ $descripcion_negocio }}' },
                { token: '{{ $logo_negocio }}' },
                { token: '{{ $tipo_negocio }}' },
                { token: '{{ $sector }}' },
                { token: '{{ $actividad_economica }}' },
                { token: '{{ $tipo_constitucion }}' },
                { token: '{{ $rango_salarial_negocio }}' },
                { token: '{{ $slug_negocio }}' },

                { type: 'header', label: 'Informacion del correo' },
                { token: '{{ $asunto }}' },
                { token: '{!! $contenido !!}' },
                { token: '{{ $difusion }}' },
                { token: '{{ $difusion_id }}' },
                { token: '{{ $traking_clicks }}' },
            ],
            testEmail: '',
            sendingTest: false,
            campaignAudience: 'all',
            sendingCampaign: false,
            campaignMessage: '',
            testModal: {
                visible: false,
            },
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                mode: '',
                blockId: null,
                targetEl: null,
                containerEl: null,
            },
            columnMenu: {
                visible: false,
                x: 0,
                y: 0,
                blockId: null,
                colIndex: null,
                targetEl: null,
                containerEl: null,
            },
            imageModal: {
                visible: false,
                mode: 'element',
                src: '',
                width: '',
                height: '',
                linkUrl: '',
                openNewTab: false,
                targetEl: null,
                blockId: null,
                blockRef: null,
                containerEl: null,
                colIndex: null,
                contextType: '',
                uploading: false,
                uploadError: '',
            },
            options: BLOCK_OPTIONS,
            blockRegistry: BLOCK_REGISTRY,
            blocks: [],
            blockEditor: {
                visible: false,
                blockId: null,
                component: null,
                props: {},
            },
            elementEditor: {
                visible: false,
                key: 'raw',
                component: MailingBlockRawEditor,
                props: {},
                targetEl: null,
                containerEl: null,
                blockId: null,
                colIndex: null,
            },
        };
    },
    computed: {
    },
    methods: {
        iconClass(icon) {
            return ICON_CLASS_MAP[icon] || 'fa-regular fa-square';
        },
        openPreview() {
            if (!this.mailingFileName) {
                return;
            }
            const base = this.previewUrlBase.replace(/\/$/, '');
            const url = `${base}/${this.mailingFileName}/preview`;
            window.open(url, '_blank', 'noopener');
        },
        openTestModal() {
            this.testModal.visible = true;
        },
        closeTestModal() {
            this.testModal.visible = false;
        },
        clearVisualEditor() {
            this.htmlDraft = '';
            this.resetHistory('');
        },
        onVisualInput(event) {
            const body = event && event.target ? event.target.innerHTML : '';
            this.htmlDraft = body;
            this.pushHistory(this.htmlDraft);
        },
        onVisualKeydown(event) {
            if (!event) {
                return;
            }
            const key = event.key ? event.key.toLowerCase() : '';
            if ((event.ctrlKey || event.metaKey) && key === 'z') {
                event.preventDefault();
                this.undoVisual();
            }
        },
        undoVisual() {
            if (this.historyIndex <= 0) {
                return;
            }
            this.historyIndex -= 1;
            const html = this.history[this.historyIndex];
            if (html === undefined) {
                return;
            }
            this.historyLocked = true;
            this.htmlDraft = html;
            const editor = this.$refs.visualEditor;
            if (editor) {
                editor.innerHTML = html;
            }
            this.historyLocked = false;
        },
        openContextMenu(block, event) {
            const containerEl = event && event.currentTarget
                ? event.currentTarget.querySelector('.raw-block-body')
                : null;
            this.contextMenu.visible = true;
            this.contextMenu.x = event.clientX;
            this.contextMenu.y = event.clientY;
            this.contextMenu.mode = 'block';
            this.contextMenu.blockId = block.id;
            this.contextMenu.targetEl = event.target && event.target.nodeType === 1
                ? event.target
                : event.target
                    ? event.target.parentElement
                    : null;
            this.contextMenu.containerEl = containerEl;
        },
        openVisualContextMenu(event) {
            const target = event.target;
            if (!target || target === event.currentTarget) {
                return;
            }
            this.contextMenu.visible = true;
            this.contextMenu.x = event.clientX;
            this.contextMenu.y = event.clientY;
            this.contextMenu.mode = 'visual';
            this.contextMenu.blockId = null;
            this.contextMenu.targetEl = target.nodeType === 1 ? target : target.parentElement;
        },
        closeContextMenu() {
            this.contextMenu.visible = false;
            this.contextMenu.blockId = null;
            this.contextMenu.targetEl = null;
            this.contextMenu.containerEl = null;
            this.contextMenu.mode = '';
        },
        closeColumnMenu() {
            this.columnMenu.visible = false;
            this.columnMenu.blockId = null;
            this.columnMenu.colIndex = null;
            this.columnMenu.targetEl = null;
            this.columnMenu.containerEl = null;
        },
        closeAllContextMenus() {
            this.closeContextMenu();
            this.closeColumnMenu();
        },
        handleContextEdit() {
            if (this.contextMenu.mode === 'block') {
                const block = this.blocks.find((item) => item.id === this.contextMenu.blockId);
                if (block && block.key === 'raw' && this.contextMenu.targetEl) {
                    this.openElementEditor({
                        targetEl: this.contextMenu.targetEl,
                        containerEl: this.contextMenu.containerEl,
                        blockId: this.contextMenu.blockId,
                    });
                    this.closeContextMenu();
                    return;
                }
                this.editBlock(this.contextMenu.blockId);
                return;
            }
            this.openElementEditor({
                targetEl: this.contextMenu.targetEl,
            });
            this.closeContextMenu();
        },
        handleContextDuplicate() {
            if (this.contextMenu.mode !== 'block') {
                return;
            }
            this.duplicateBlock(this.contextMenu.blockId);
            this.closeContextMenu();
        },
        handleContextDelete() {
            if (this.contextMenu.mode === 'block') {
                this.removeBlock(this.contextMenu.blockId);
                return;
            }
            this.removeVisualTarget();
        },
        openColumnContextMenu(block, colIndex, event) {
            if (!block || !event) {
                return;
            }
            this.columnMenu.visible = true;
            this.columnMenu.x = event.clientX;
            this.columnMenu.y = event.clientY;
            this.columnMenu.blockId = block.id;
            this.columnMenu.colIndex = colIndex;
            this.columnMenu.targetEl = event.target.nodeType === 1 ? event.target : event.target.parentElement;
            this.columnMenu.containerEl = event.currentTarget;
            this.closeContextMenu();
        },
        handleColumnEdit() {
            const target = this.columnMenu.targetEl;
            if (!target) {
                this.closeColumnMenu();
                return;
            }
            if (target.tagName && target.tagName.toLowerCase() === 'img') {
                this.openImageModal(target);
                this.closeColumnMenu();
                return;
            }
            this.openElementEditor({
                targetEl: target,
                containerEl: this.columnMenu.containerEl,
                blockId: this.columnMenu.blockId,
                colIndex: this.columnMenu.colIndex,
            });
            this.closeColumnMenu();
        },
        handleColumnDelete() {
            const target = this.columnMenu.targetEl;
            if (target && this.columnMenu.containerEl) {
                target.remove();
                this.commitColumnContent(this.columnMenu.blockId, this.columnMenu.colIndex, this.columnMenu.containerEl);
            }
            this.closeColumnMenu();
        },
        commitColumnContent(blockId, colIndex, containerEl) {
            const block = this.blocks.find((item) => item.id === blockId);
            if (!block || !containerEl) {
                return;
            }
            if (colIndex === null || colIndex === undefined) {
                return;
            }
            block.props.contents[colIndex] = containerEl.innerHTML;
            this.updateBlockHtml(block);
        },
        onColumnDrop(block, colIndex, event) {
            if (!block || !event || colIndex === undefined || colIndex === null) {
                return;
            }
            event.stopPropagation();
            const payload = this.parseDropData(event.dataTransfer.getData('text/plain'));
            if (payload.type === 'new' && payload.key) {
                const newBlock = this.createBlockFromType(payload.key);
                if (newBlock) {
                    const existing = block.props.contents[colIndex] || '';
                    block.props.contents[colIndex] = `${existing}${newBlock.html}`;
                    this.updateBlockHtml(block);
                }
                this.resetDragState();
                return;
            }
            if (payload.type === 'move' && payload.id) {
                const fromIndex = this.blocks.findIndex((item) => item.id === payload.id);
                if (fromIndex === -1) {
                    return;
                }
                const movedBlock = this.blocks[fromIndex];
                const existing = block.props.contents[colIndex] || '';
                block.props.contents[colIndex] = `${existing}${movedBlock.html}`;
                this.blocks.splice(fromIndex, 1);
                this.updateBlockHtml(block);
            }
            this.resetDragState();
        },
        onColumnClick(event) {
            if (!event || !event.target) {
                return;
            }
            const target = event.target;
            if (target.tagName && target.tagName.toLowerCase() === 'img') {
                this.openImageModal(target);
            }
        },
        isColumnsBlock(block) {
            return block && block.key && block.key.startsWith('col-');
        },
        editBlock(blockId) {
            const block = this.blocks.find((item) => item.id === blockId);
            if (!block) {
                return;
            }
            if (block.key === 'image') {
                this.openImageModalForBlock(block);
                this.closeContextMenu();
                return;
            }
            this.openBlockEditor(block);
            this.closeContextMenu();
        },
        removeVisualTarget() {
            const target = this.contextMenu.targetEl;
            if (!target) {
                this.closeContextMenu();
                return;
            }
            target.remove();
            this.syncVisualHtml();
            this.closeContextMenu();
        },
        openElementEditor({ targetEl, containerEl = null, blockId = null, colIndex = null }) {
            if (!targetEl) {
                return;
            }
            const config = this.resolveElementEditor(targetEl);
            this.elementEditor = {
                visible: true,
                key: config.key,
                component: config.component,
                props: this.cloneProps(config.props || {}),
                targetEl: config.targetEl || targetEl,
                containerEl,
                blockId,
                colIndex,
            };
        },
        closeElementEditor() {
            this.elementEditor.visible = false;
            this.elementEditor.key = 'raw';
            this.elementEditor.component = MailingBlockRawEditor;
            this.elementEditor.props = {};
            this.elementEditor.targetEl = null;
            this.elementEditor.containerEl = null;
            this.elementEditor.blockId = null;
            this.elementEditor.colIndex = null;
        },
        saveElementEditor() {
            const target = this.elementEditor.targetEl;
            if (!target) {
                this.closeElementEditor();
                return;
            }
            let html = '';
            if (this.elementEditor.key === 'raw') {
                html = (this.elementEditor.props.html || '').trim();
            } else {
                const blockType = this.blockRegistry[this.elementEditor.key];
                html = blockType && blockType.buildHtml ? blockType.buildHtml(this.elementEditor.props || {}) : '';
            }
            if (!html) {
                target.remove();
            } else {
                const wrapper = document.createElement('div');
                wrapper.innerHTML = html;
                const replacement = wrapper.firstElementChild;
                if (replacement) {
                    target.replaceWith(replacement);
                }
            }
            if (this.elementEditor.blockId && this.elementEditor.colIndex !== null) {
                this.commitColumnContent(this.elementEditor.blockId, this.elementEditor.colIndex, this.elementEditor.containerEl);
            } else if (this.elementEditor.blockId && this.elementEditor.containerEl) {
                const block = this.blocks.find((item) => item.id === this.elementEditor.blockId);
                if (block && block.key === 'raw') {
                    block.html = this.elementEditor.containerEl.innerHTML;
                    this.syncHtmlFromBlocks();
                    this.pushHistory(this.htmlDraft);
                } else {
                    this.syncVisualHtml();
                }
            } else {
                this.syncVisualHtml();
            }
            this.closeElementEditor();
        },
        resolveElementEditor(targetEl) {
            const tag = targetEl.tagName ? targetEl.tagName.toLowerCase() : '';
            const paragraph = targetEl.closest ? targetEl.closest('p') : null;
            if (paragraph) {
                const props = this.propsFromTextElement(paragraph);
                return {
                    key: 'text',
                    component: this.blockRegistry.text.editorComponent,
                    props,
                    targetEl: paragraph,
                };
            }
            const heading = targetEl.closest ? targetEl.closest('h1,h2,h3,h4,h5,h6') : null;
            if (heading) {
                const props = this.propsFromTitleElement(heading);
                return {
                    key: 'title',
                    component: this.blockRegistry.title.editorComponent,
                    props,
                    targetEl: heading,
                };
            }
            const anchor = tag === 'a'
                ? targetEl
                : targetEl.querySelector
                    ? targetEl.querySelector('a')
                    : null;
            if (anchor) {
                const resolved = this.propsFromButtonElement(anchor);
                return {
                    key: 'button',
                    component: this.blockRegistry.button.editorComponent,
                    props: resolved.props,
                    targetEl: resolved.targetEl,
                };
            }
            return {
                key: 'raw',
                component: MailingBlockRawEditor,
                props: { html: targetEl.outerHTML || '' },
                targetEl,
            };
        },
        propsFromTextElement(targetEl) {
            const defaults = this.blockRegistry.text.defaultProps || {};
            const styles = this.parseInlineStyles(targetEl.getAttribute('style'));
            return {
                ...this.cloneProps(defaults),
                content: targetEl.innerHTML || '',
                color: styles.color || defaults.color,
                fontSize: styles['font-size'] || defaults.fontSize,
                fontFamily: styles['font-family'] || defaults.fontFamily,
                lineHeight: styles['line-height'] || defaults.lineHeight,
                align: styles['text-align'] || defaults.align,
                padding: styles.padding || defaults.padding,
            };
        },
        propsFromTitleElement(targetEl) {
            const defaults = this.blockRegistry.title.defaultProps || {};
            const styles = this.parseInlineStyles(targetEl.getAttribute('style'));
            return {
                ...this.cloneProps(defaults),
                tag: targetEl.tagName.toLowerCase(),
                text: targetEl.innerHTML || '',
                color: styles.color || defaults.color,
                fontSize: styles['font-size'] || defaults.fontSize,
                fontFamily: styles['font-family'] || defaults.fontFamily,
                lineHeight: styles['line-height'] || defaults.lineHeight,
                align: styles['text-align'] || defaults.align,
                padding: styles.padding || defaults.padding,
            };
        },
        propsFromButtonElement(targetEl) {
            const defaults = this.blockRegistry.button.defaultProps || {};
            const wrapper = targetEl.parentElement && targetEl.parentElement.tagName
                && targetEl.parentElement.tagName.toLowerCase() === 'div'
                ? targetEl.parentElement
                : targetEl;
            const wrapperStyles = this.parseInlineStyles(wrapper.getAttribute('style'));
            const buttonStyles = this.parseInlineStyles(targetEl.getAttribute('style'));
            return {
                props: {
                    ...this.cloneProps(defaults),
                    text: targetEl.textContent || defaults.text,
                    url: targetEl.getAttribute('href') || defaults.url,
                    backgroundColor: buttonStyles.background || buttonStyles['background-color'] || defaults.backgroundColor,
                    textColor: buttonStyles.color || defaults.textColor,
                    padding: buttonStyles.padding || defaults.padding,
                    borderRadius: buttonStyles['border-radius'] || defaults.borderRadius,
                    fontSize: buttonStyles['font-size'] || defaults.fontSize,
                    fontFamily: buttonStyles['font-family'] || defaults.fontFamily,
                    align: wrapperStyles['text-align'] || defaults.align,
                },
                targetEl: wrapper,
            };
        },
        parseInlineStyles(value) {
            const styles = {};
            if (!value) {
                return styles;
            }
            value.split(';').forEach((chunk) => {
                const [rawKey, rawVal] = chunk.split(':');
                if (!rawKey || !rawVal) {
                    return;
                }
                const key = rawKey.trim().toLowerCase();
                const val = rawVal.trim();
                if (key) {
                    styles[key] = val;
                }
            });
            return styles;
        },
        syncVisualHtml() {
            this.pushHistory(this.htmlDraft);
        },
        updateBlockHtml(block) {
            const blockType = this.blockRegistry[block.key];
            if (!blockType || !blockType.buildHtml) {
                return;
            }
            block.html = blockType.buildHtml(block.props);
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
        },
        openImageModal(target) {
            const linkEl = target.closest('a');
            const columnContainer = target.closest ? target.closest('.column-drop') : null;
            const rawContainer = target.closest ? target.closest('.raw-block-body') : null;
            let contextType = '';
            let blockId = null;
            let colIndex = null;
            let containerEl = null;
            if (columnContainer) {
                contextType = 'column';
                blockId = columnContainer.dataset ? columnContainer.dataset.blockId : null;
                colIndex = columnContainer.dataset ? Number(columnContainer.dataset.colIndex) : null;
                containerEl = columnContainer;
            } else if (rawContainer) {
                contextType = 'raw';
                containerEl = rawContainer;
                const blockEl = rawContainer.closest ? rawContainer.closest('.visual-block') : null;
                blockId = blockEl && blockEl.dataset ? blockEl.dataset.blockId : null;
            }
            this.imageModal = {
                visible: true,
                mode: 'element',
                src: target.getAttribute('src') || '',
                width: target.style.width || target.getAttribute('width') || '',
                height: target.style.height || target.getAttribute('height') || '',
                linkUrl: linkEl ? linkEl.getAttribute('href') || '' : '',
                openNewTab: linkEl ? linkEl.getAttribute('target') === '_blank' : false,
                targetEl: target,
                blockId,
                blockRef: null,
                containerEl,
                colIndex,
                contextType,
                uploading: false,
                uploadError: '',
            };
            this.closeContextMenu();
        },
        openImageModalForBlock(block) {
            this.imageModal = {
                visible: true,
                mode: 'block',
                src: block.props.src || '',
                width: block.props.width || '',
                height: block.props.height || '',
                linkUrl: block.props.linkUrl || '',
                openNewTab: !!block.props.openNewTab,
                targetEl: null,
                blockId: block.id,
                blockRef: block,
                containerEl: null,
                colIndex: null,
                contextType: '',
                uploading: false,
                uploadError: '',
            };
        },
        closeImageModal() {
            this.imageModal.visible = false;
            this.imageModal.targetEl = null;
            this.imageModal.blockId = null;
            this.imageModal.blockRef = null;
            this.imageModal.containerEl = null;
            this.imageModal.colIndex = null;
            this.imageModal.contextType = '';
            this.imageModal.mode = 'element';
        },
        saveImageModal() {
            if (this.imageModal.mode === 'block') {
                const block = this.imageModal.blockRef
                    ? this.imageModal.blockRef
                    : this.blocks.find((item) => item.id === this.imageModal.blockId);
                if (!block) {
                    this.closeImageModal();
                    return;
                }
                block.props = {
                    ...block.props,
                    src: this.imageModal.src.trim(),
                    alt: this.imageNameFromSrc(this.imageModal.src.trim()),
                    width: this.imageModal.width.trim(),
                    height: this.imageModal.height.trim(),
                    linkUrl: this.imageModal.linkUrl.trim(),
                    openNewTab: !!this.imageModal.openNewTab,
                };
                this.updateBlockHtml(block);
                this.closeImageModal();
                return;
            }
            const target = this.imageModal.targetEl;
            if (!target) {
                this.closeImageModal();
                return;
            }
            const src = this.imageModal.src.trim();
            if (src) {
                target.setAttribute('src', src);
                const alt = this.imageNameFromSrc(src);
                if (alt) {
                    target.setAttribute('alt', alt);
                }
            }
            const width = this.imageModal.width.trim();
            const height = this.imageModal.height.trim();
            if (width) {
                target.style.width = width;
            } else {
                target.style.width = '800px';
            }
            if (height) {
                target.style.height = height;
            } else {
                target.style.removeProperty('height');
                target.removeAttribute('height');
            }
            target.style.maxWidth = '600px';
            const linkUrl = this.imageModal.linkUrl.trim();
            if (linkUrl) {
                let linkEl = target.closest('a');
                if (!linkEl) {
                    linkEl = document.createElement('a');
                    target.replaceWith(linkEl);
                    linkEl.appendChild(target);
                }
                linkEl.setAttribute('href', linkUrl);
                if (this.imageModal.openNewTab) {
                    linkEl.setAttribute('target', '_blank');
                    linkEl.setAttribute('rel', 'noopener');
                } else {
                    linkEl.removeAttribute('target');
                    linkEl.removeAttribute('rel');
                }
            } else {
                const linkEl = target.closest('a');
                if (linkEl) {
                    linkEl.replaceWith(target);
                }
            }
            if (this.imageModal.contextType === 'column' && this.imageModal.blockId && this.imageModal.containerEl) {
                this.commitColumnContent(this.imageModal.blockId, this.imageModal.colIndex, this.imageModal.containerEl);
            } else if (this.imageModal.contextType === 'raw' && this.imageModal.blockId && this.imageModal.containerEl) {
                const block = this.blocks.find((item) => item.id === this.imageModal.blockId);
                if (block && block.key === 'raw') {
                    block.html = this.imageModal.containerEl.innerHTML;
                    this.syncHtmlFromBlocks();
                    this.pushHistory(this.htmlDraft);
                } else {
                    this.syncVisualHtml();
                }
            } else {
                this.syncVisualHtml();
            }
            this.closeImageModal();
        },
        openBlockEditor(block) {
            if (!block) {
                return;
            }
            const editorComponent = this.resolveEditorComponent(block);
            if (!editorComponent) {
                return;
            }
            const props = this.resolveEditorProps(block);
            this.blockEditor = {
                visible: true,
                blockId: block.id,
                component: editorComponent,
                props: this.cloneProps(props),
            };
        },
        closeBlockEditor() {
            this.blockEditor.visible = false;
            this.blockEditor.blockId = null;
            this.blockEditor.component = null;
            this.blockEditor.props = {};
        },
        saveBlockEditor() {
            const block = this.blocks.find((item) => item.id === this.blockEditor.blockId);
            if (!block) {
                this.closeBlockEditor();
                return;
            }
            block.props = this.cloneProps(this.blockEditor.props || {});
            const blockType = this.blockRegistry[block.key] || this.blockRegistry.raw;
            block.html = blockType && blockType.buildHtml ? blockType.buildHtml(block.props) : block.html;
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
            this.syncVisualEditorFromHtmlDraft();
            this.closeBlockEditor();
        },
        resolveEditorComponent(block) {
            if (block.editorComponent) {
                return block.editorComponent;
            }
            const blockType = this.blockRegistry[block.key];
            if (blockType && blockType.editorComponent) {
                return blockType.editorComponent;
            }
            return this.blockRegistry.raw ? this.blockRegistry.raw.editorComponent : null;
        },
        resolveEditorProps(block) {
            if (block.props && Object.keys(block.props).length) {
                return block.props;
            }
            if (block.key === 'raw') {
                return { html: block.html || '' };
            }
            const blockType = this.blockRegistry[block.key];
            if (blockType && blockType.defaultProps) {
                return blockType.defaultProps;
            }
            return { html: block.html || '' };
        },
        syncHtmlFromBlocks() {
            this.htmlDraft = this.blocks.map((block) => block.html).join('');
            this.syncVisualEditorFromHtmlDraft();
        },
        scheduleBlocksSyncFromHtml() {
            clearTimeout(this.htmlSyncTimer);
            this.htmlSyncTimer = setTimeout(() => {
                this.syncBlocksFromHtml(false);
            }, 250);
        },
        syncBlocksFromHtml(force) {
            const html = this.stripMailingHeader(this.htmlDraft || '');
            if (!html) {
                this.blocks = [];
                return;
            }
            const parsedBlocks = this.parseHtmlToBlocks(html);
            if (parsedBlocks.length > 0) {
                this.blocks = parsedBlocks;
                return;
            }
            if (force) {
                const rawBlock = this.createBlockFromType('raw', { html });
                this.blocks = rawBlock ? [rawBlock] : [];
            }
        },
        syncVisualEditorFromHtmlDraft() {
            return;
        },
        cloneProps(value) {
            return JSON.parse(JSON.stringify(value || {}));
        },
        imageNameFromSrc(src) {
            const cleaned = src.split('?')[0].split('#')[0];
            const lastPart = cleaned.split('/').pop() || '';
            return lastPart || 'imagen';
        },
        async onImageFileChange(event) {
            const file = event && event.target && event.target.files ? event.target.files[0] : null;
            if (!file) {
                return;
            }
            await this.uploadImageFile(file);
            event.target.value = '';
        },
        async uploadImageFile(file) {
            if (!this.apiImageUploadUrl) {
                this.imageModal.uploadError = 'No hay URL configurada para subir imagenes.';
                return;
            }
            this.imageModal.uploadError = '';
            this.imageModal.uploading = true;
            try {
                const formData = new FormData();
                formData.append('image', file);
                const response = await fetch(this.apiImageUploadUrl, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: formData,
                });
                if (!response.ok) {
                    let message = 'No se pudo subir la imagen.';
                    try {
                        const data = await response.json();
                        if (data && data.errors && data.errors.image && data.errors.image[0]) {
                            message = data.errors.image[0];
                        } else if (data && data.message) {
                            message = data.message;
                        }
                    } catch (e) {
                        // noop
                    }
                    this.imageModal.uploadError = message;
                    return;
                }
                const data = await response.json();
                if (data && data.url) {
                    this.imageModal.src = data.url;
                    this.showToast('Imagen subida');
                } else {
                    this.imageModal.uploadError = 'No se recibio la URL publica.';
                }
            } catch (e) {
                this.imageModal.uploadError = 'No se pudo subir la imagen.';
            } finally {
                this.imageModal.uploading = false;
            }
        },
        async onAttachmentChange(event) {
            const file = event && event.target && event.target.files ? event.target.files[0] : null;
            if (!file) {
                return;
            }
            await this.uploadAttachmentFile(file);
            event.target.value = '';
        },
        async uploadAttachmentFile(file) {
            if (!this.apiAttachmentUploadUrl) {
                this.attachmentError = 'No hay URL configurada para subir adjuntos.';
                return;
            }
            this.attachmentError = '';
            this.attachmentUploading = true;
            try {
                const formData = new FormData();
                formData.append('attachment', file);
                const response = await fetch(this.apiAttachmentUploadUrl, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: formData,
                });
                if (!response.ok) {
                    let message = 'No se pudo subir el adjunto.';
                    try {
                        const data = await response.json();
                        if (data && data.errors && data.errors.attachment && data.errors.attachment[0]) {
                            message = data.errors.attachment[0];
                        } else if (data && data.message) {
                            message = data.message;
                        }
                    } catch (e) {
                        // noop
                    }
                    this.attachmentError = message;
                    return;
                }
                const data = await response.json();
                if (data && data.path) {
                    this.mailingAttachments.push({
                        name: data.name || file.name,
                        path: data.path,
                        url: data.url || '',
                    });
                    if (this.mailingFileName || this.templateSlugValue) {
                        await this.updateMailing();
                    }
                } else {
                    this.attachmentError = 'No se recibio la informacion del adjunto.';
                }
            } catch (e) {
                this.attachmentError = 'No se pudo subir el adjunto.';
            } finally {
                this.attachmentUploading = false;
            }
        },
        removeAttachment(index) {
            this.mailingAttachments.splice(index, 1);
            if (this.mailingFileName || this.templateSlugValue) {
                this.updateMailing();
            }
        },
        getCsrfToken() {
            const meta = document.querySelector('meta[name="csrf-token"]');
            return meta ? meta.getAttribute('content') : '';
        },
        async fetchMailingMeta() {
            if (!this.mailingFileName) {
                return;
            }
            try {
                const response = await fetch(`${this.apiMetaUrl}?slug=${encodeURIComponent(this.mailingFileName)}`);
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                if (data && data.name) {
                    this.mailingName = data.name;
                }
                this.mailingSubject = data && data.subject ? data.subject : '';
                this.mailingAttachments = data && Array.isArray(data.attachments) ? data.attachments : [];
                this.refreshSnapshot();
            } catch (e) {
                // noop
            }
        },
        async fetchMailingContent() {
            if (!this.mailingFileName) {
                return;
            }
            try {
                const response = await fetch(`${this.apiContentUrl}?slug=${encodeURIComponent(this.mailingFileName)}`, {
                    headers: {
                        'X-APP-TOKEN': this.apiToken,
                    },
                });
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                if (data && data.html) {
                    this.htmlDraft = this.extractContenidoFromHtml(this.stripMailingHeader(data.html));
                    this.blocks = [];
                    this.ensureRawBlockForExistingHtml();
                    this.resetHistory(this.htmlDraft);
                    this.refreshSnapshot();
                }
            } catch (e) {
                // noop
            }
        },
        async createMailing() {
            if (!this.mailingName.trim()) {
                alert('Ingresa un nombre para el mailing.');
                return;
            }
            this.creating = true;
            try {
                const response = await fetch(this.apiCreateUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: JSON.stringify({
                        name: this.mailingName,
                        subject: this.mailingSubject,
                        attachments: this.mailingAttachments,
                    }),
                });
                if (!response.ok) {
                    throw new Error('create failed');
                }
                const data = await response.json();
                this.templateSlugValue = data.slug || '';
                if (this.templateSlugValue) {
                    this.mailingFileName = this.templateSlugValue;
                    window.location.href = `${this.editUrlBase.replace(/\/$/, '')}/${this.templateSlugValue}/edit`;
                }
            } catch (e) {
                alert('No se pudo crear el mailing.');
            } finally {
                this.creating = false;
            }
        },
        onDragStart(option, event) {
            if (!event || !event.dataTransfer) {
                return;
            }
            this.dragActive = true;
            event.dataTransfer.effectAllowed = 'copy';
            event.dataTransfer.setData('text/plain', `new:${option.key}`);
        },
        onDrop(event) {
            if (!event || !event.dataTransfer) {
                return;
            }
            if (this.editorMode === 'visual') {
                return;
            }
            const payload = this.parseDropData(event.dataTransfer.getData('text/plain'));
            if (payload.type === 'new' && payload.key) {
                this.insertBlockAt(this.blocks.length, payload.key);
                return;
            }
            if (payload.type === 'move' && payload.id) {
                this.moveBlock(payload.id, this.blocks.length);
            }
            this.resetDragState();
        },
        addBlock(option) {
            if (!option || !option.key) {
                return;
            }
            this.insertBlockAt(this.blocks.length, option.key);
        },
        onBlockDragStart(block, event) {
            if (!event || !event.dataTransfer || !block) {
                return;
            }
            this.dragActive = true;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', `move:${block.id}`);
        },
        handleDragEnd() {
            this.resetDragState();
        },
        handleBlockDragOver(index, event) {
            if (!this.dragActive) {
                return;
            }
            const target = event && event.currentTarget ? event.currentTarget : null;
            if (!target || typeof target.getBoundingClientRect !== 'function') {
                this.dragOverIndex = index;
                return;
            }
            const rect = target.getBoundingClientRect();
            const offsetY = event.clientY - rect.top;
            const ratio = rect.height ? offsetY / rect.height : 0.5;
            if (ratio < 0.4) {
                this.dragOverIndex = Math.min(Math.max(index, 0), this.blocks.length);
                return;
            }
            if (ratio > 0.6) {
                this.dragOverIndex = Math.min(Math.max(index + 1, 0), this.blocks.length);
            }
        },
        handleCanvasDragOver(event) {
            if (!this.dragActive) {
                return;
            }
            if (event && event.target !== event.currentTarget) {
                return;
            }
            const container = event.currentTarget;
            if (!container || typeof container.querySelectorAll !== 'function') {
                this.dragOverIndex = this.blocks.length;
                return;
            }
            const blockEls = Array.from(container.querySelectorAll('.visual-block'));
            if (!blockEls.length) {
                this.dragOverIndex = this.blocks.length;
                return;
            }
            const pointerY = event.clientY;
            let nextIndex = this.blocks.length;
            for (const blockEl of blockEls) {
                const rect = blockEl.getBoundingClientRect();
                const midY = rect.top + rect.height / 2;
                if (pointerY < midY) {
                    const blockId = blockEl.dataset ? blockEl.dataset.blockId : null;
                    const idx = this.blocks.findIndex((item) => item.id === blockId);
                    nextIndex = idx === -1 ? 0 : idx;
                    break;
                }
            }
            this.dragOverIndex = Math.min(Math.max(nextIndex, 0), this.blocks.length);
        },
        handleBlockDragLeave() {
            if (!this.dragActive) {
                return;
            }
            clearTimeout(this.dragLeaveTimer);
            this.dragLeaveTimer = setTimeout(() => {
                this.dragOverIndex = null;
            }, 120);
        },
        onBlockDrop(index, event) {
            if (!event || !event.dataTransfer) {
                return;
            }
            event.stopPropagation();
            const payload = this.parseDropData(event.dataTransfer.getData('text/plain'));
            if (payload.type === 'new' && payload.key) {
                this.insertBlockAt(index, payload.key);
                return;
            }
            if (payload.type === 'move' && payload.id) {
                this.moveBlock(payload.id, index);
            }
            this.resetDragState();
        },
        parseDropData(value) {
            if (!value || typeof value !== 'string') {
                return { type: '', key: '', id: '' };
            }
            const [type, payload] = value.split(':');
            return { type, key: type === 'new' ? payload : '', id: type === 'move' ? payload : '' };
        },
        resetDragState() {
            this.dragActive = false;
            this.dragOverIndex = null;
            clearTimeout(this.dragLeaveTimer);
            this.dragLeaveTimer = null;
        },
        showDropPlaceholder(index) {
            return this.dragActive && this.dragOverIndex === index;
        },
        ensureRawBlockForExistingHtml() {
            if (this.blocks.length > 0) {
                return;
            }
            if (!this.htmlDraft) {
                return;
            }
            const parsedBlocks = this.parseHtmlToBlocks(this.htmlDraft);
            if (parsedBlocks.length > 0) {
                this.blocks.push(...parsedBlocks);
                this.syncHtmlFromBlocks();
                return;
            }
            const rawBlock = this.createBlockFromType('raw', { html: this.htmlDraft });
            if (rawBlock) {
                this.blocks.push(rawBlock);
            }
        },
        createBlockFromType(key, propsOverride = null) {
            const blockType = this.blockRegistry[key];
            if (!blockType) {
                return null;
            }
            const props = this.cloneProps(propsOverride || blockType.defaultProps || {});
            const html = blockType.buildHtml ? blockType.buildHtml(props) : '';
            return {
                id: Date.now() + Math.random().toString(16).slice(2),
                key,
                label: blockType.label || key,
                html,
                component: blockType.component,
                componentProps: blockType.componentProps || {},
                editorComponent: blockType.editorComponent,
                props,
            };
        },
        insertBlockAt(index, key) {
            this.ensureRawBlockForExistingHtml();
            const block = this.createBlockFromType(key);
            if (!block) {
                return;
            }
            const safeIndex = Math.max(0, Math.min(index, this.blocks.length));
            this.blocks.splice(safeIndex, 0, block);
            this.queueAutoSave();
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
        },
        moveBlock(id, toIndex) {
            const fromIndex = this.blocks.findIndex((block) => block.id === id);
            if (fromIndex === -1) {
                return;
            }
            const safeIndex = Math.max(0, Math.min(toIndex, this.blocks.length - 1));
            if (fromIndex === safeIndex) {
                return;
            }
            const [block] = this.blocks.splice(fromIndex, 1);
            this.blocks.splice(safeIndex, 0, block);
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
        },
        duplicateBlock(blockId) {
            const fromIndex = this.blocks.findIndex((block) => block.id === blockId);
            if (fromIndex === -1) {
                return;
            }
            const source = this.blocks[fromIndex];
            const blockType = this.blockRegistry[source.key];
            const props = this.cloneProps(source.props || {});
            const html = blockType && blockType.buildHtml
                ? blockType.buildHtml(props)
                : source.html;
            const copy = {
                id: Date.now() + Math.random().toString(16).slice(2),
                key: source.key,
                label: source.label || (blockType ? blockType.label : source.key),
                html,
                component: blockType ? blockType.component : source.component,
                componentProps: blockType ? (blockType.componentProps || {}) : (source.componentProps || {}),
                editorComponent: blockType ? blockType.editorComponent : source.editorComponent,
                props,
            };
            this.blocks.splice(fromIndex + 1, 0, copy);
            this.queueAutoSave();
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
        },
        parseHtmlToBlocks(html) {
            if (!html) {
                return [];
            }
            let doc = null;
            try {
                doc = new DOMParser().parseFromString(html, 'text/html');
            } catch (e) {
                return [];
            }
            if (!doc || !doc.body) {
                return [];
            }
            const blocks = [];
            doc.body.childNodes.forEach((node) => {
                if (node.nodeType !== 1) {
                    return;
                }
                const tag = node.tagName.toLowerCase();
                if (tag === 'table') {
                    const colsBlock = this.tableToColumnsBlock(node);
                    if (colsBlock) {
                        blocks.push(colsBlock);
                        return;
                    }
                }
                if (tag === 'div') {
                    const elementChildren = Array.from(node.children || []);
                    if (elementChildren.length === 1) {
                        const child = elementChildren[0];
                        const childTag = child.tagName.toLowerCase();
                        const wrapperStyles = this.parseInlineStyles(node.getAttribute('style'));
                        if (childTag === 'p') {
                            const defaults = this.blockRegistry.text.defaultProps;
                            const styles = this.parseInlineStyles(child.getAttribute('style'));
                            const block = this.createBlockFromType('text', {
                                ...defaults,
                                content: child.innerHTML,
                                color: styles.color || defaults.color,
                                fontSize: styles['font-size'] || defaults.fontSize,
                                fontFamily: styles['font-family'] || defaults.fontFamily,
                                lineHeight: styles['line-height'] || defaults.lineHeight,
                                align: styles['text-align'] || wrapperStyles['text-align'] || defaults.align,
                                padding: wrapperStyles.padding || styles.padding || defaults.padding,
                            });
                            if (block) {
                                blocks.push(block);
                            }
                            return;
                        }
                        if (childTag === 'h1' || childTag === 'h2' || childTag === 'h3') {
                            const defaults = this.blockRegistry.title.defaultProps;
                            const styles = this.parseInlineStyles(child.getAttribute('style'));
                            const block = this.createBlockFromType('title', {
                                ...defaults,
                                tag: childTag,
                                text: child.innerHTML,
                                color: styles.color || defaults.color,
                                fontSize: styles['font-size'] || defaults.fontSize,
                                fontFamily: styles['font-family'] || defaults.fontFamily,
                                lineHeight: styles['line-height'] || defaults.lineHeight,
                                align: styles['text-align'] || wrapperStyles['text-align'] || defaults.align,
                                padding: wrapperStyles.padding || styles.padding || defaults.padding,
                            });
                            if (block) {
                                blocks.push(block);
                            }
                            return;
                        }
                        if (childTag === 'a') {
                            const defaults = this.blockRegistry.button.defaultProps;
                            const styles = this.parseInlineStyles(child.getAttribute('style'));
                            const block = this.createBlockFromType('button', {
                                ...defaults,
                                text: child.textContent || defaults.text,
                                url: child.getAttribute('href') || defaults.url,
                                backgroundColor: styles.background || defaults.backgroundColor,
                                textColor: styles.color || defaults.textColor,
                                padding: styles.padding || defaults.padding,
                                borderRadius: styles['border-radius'] || defaults.borderRadius,
                                fontSize: styles['font-size'] || defaults.fontSize,
                                fontFamily: styles['font-family'] || defaults.fontFamily,
                                align: wrapperStyles['text-align'] || defaults.align,
                            });
                            if (block) {
                                blocks.push(block);
                            }
                            return;
                        }
                    }
                }
                if (tag === 'p') {
                    const block = this.createBlockFromType('text', { ...this.blockRegistry.text.defaultProps, content: node.innerHTML });
                    if (block) {
                        blocks.push(block);
                    }
                    return;
                }
                if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
                    const block = this.createBlockFromType('title', { ...this.blockRegistry.title.defaultProps, tag, text: node.innerHTML });
                    if (block) {
                        blocks.push(block);
                    }
                    return;
                }
                const block = this.createBlockFromType('raw', { html: node.outerHTML });
                if (block) {
                    blocks.push(block);
                }
            });
            return blocks;
        },
        tableToColumnsBlock(tableEl) {
            if (!tableEl) {
                return null;
            }
            const tds = Array.from(tableEl.querySelectorAll('tr:first-child > td'));
            if (!tds.length) {
                return null;
            }
            const contents = tds.map((td) => td.innerHTML.trim());
            const columns = Math.min(Math.max(contents.length, 1), 3);
            const widthDefaults = columns === 2 ? ['50%', '50%'] : columns === 3 ? ['33.33%', '33.33%', '33.33%'] : ['100%'];
            const props = {
                ...this.blockRegistry['col-1'].defaultProps,
                columns,
                contents: contents.slice(0, columns),
                widths: widthDefaults.slice(0, columns),
            };
            const key = `col-${columns}`;
            return this.createBlockFromType(key, props);
        },
        removeBlock(id) {
            this.blocks = this.blocks.filter((block) => block.id !== id);
            this.queueAutoSave();
            this.syncHtmlFromBlocks();
            this.pushHistory(this.htmlDraft);
            this.closeContextMenu();
        },
        queueAutoSave() {
            return;
            if (!this.templateSlugValue) {
                return;
            }
            clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = setTimeout(() => {
                this.updateMailing();
            }, 400);
        },
        async updateMailing() {
            if (!this.templateSlugValue) {
                return;
            }
            const html = this.currentHtml();
            this.htmlDraft = html;
            const response = await fetch(this.apiUpdateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                    'X-APP-TOKEN': this.apiToken,
                },
                body: JSON.stringify({
                    slug: this.mailingFileName || this.templateSlugValue,
                    name: this.mailingName,
                    subject: this.mailingSubject,
                    attachments: this.mailingAttachments,
                    html,
                }),
            });
            if (response.ok) {
                this.refreshSnapshot();
            }
            return response;
        },
        async update() {
            if (!this.templateSlugValue) {
                return;
            }
            if (this.editorMode === 'visual' && this.blocks.length) {
                this.syncHtmlFromBlocks();
            }
            try {
                const response = await fetch(this.apiUpdateUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: JSON.stringify({
                        slug: this.mailingFileName || this.templateSlugValue,
                        name: this.mailingName,
                        subject: this.mailingSubject,
                        attachments: this.mailingAttachments,
                        html: this.currentHtml(),
                    }),
                });
                if (!response.ok) {
                    throw new Error('update failed');
                }
                this.showToast('Actualizado correctamente');
                this.refreshSnapshot();
            } catch (e) {
                this.showToast('No se pudo actualizar');
            }
        },
        async sendTestEmail() {
            if (!this.mailingFileName) {
                return;
            }
            if (!this.testEmail.trim()) {
                alert('Ingresa un correo para la prueba.');
                return;
            }
            this.sendingTest = true;
            try {
                await this.updateMailing();
                const response = await fetch(this.apiTestSendUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: JSON.stringify({
                        email: this.testEmail.trim(),
                        subject: this.mailingSubject ? this.mailingSubject.trim() : undefined,
                        attachments: this.mailingAttachments,
                        slug: this.mailingFileName,
                    }),
                });
                if (!response.ok) {
                    throw new Error('test send failed');
                }
                this.showToast('Correo de prueba enviado');
                this.closeTestModal();
            } catch (e) {
                this.showToast('No se pudo enviar la prueba');
            } finally {
                this.sendingTest = false;
            }
        },
        async sendCampaign() {
            if (!this.mailingFileName) {
                return;
            }
            if (!window.confirm('¿Enviar este mailing a los destinatarios seleccionados?')) {
                return;
            }
            this.sendingCampaign = true;
            this.campaignMessage = '';
            try {
                await this.updateMailing();
                const response = await fetch(this.apiCampaignSendUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.getCsrfToken(),
                        'X-APP-TOKEN': this.apiToken,
                    },
                    body: JSON.stringify({
                        slug: this.mailingFileName,
                        subject: this.mailingSubject ? this.mailingSubject.trim() : undefined,
                        attachments: this.mailingAttachments,
                        audience: this.campaignAudience,
                    }),
                });
                if (!response.ok) {
                    throw new Error('campaign send failed');
                }
                const data = await response.json();
                this.campaignMessage = `Campaña enviada. Correos enviados: ${Number(data.sent || 0)}.`;
                this.showToast('Campaña enviada');
            } catch (e) {
                this.campaignMessage = 'No se pudo enviar la campaña.';
                this.showToast('No se pudo enviar la campaña');
            } finally {
                this.sendingCampaign = false;
            }
        },
        async copyVariable(token) {
            if (!token) {
                return;
            }
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(token);
                } else {
                    const input = document.createElement('input');
                    input.value = token;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }
                this.showToast('Variable copiada');
            } catch (e) {
                // noop
            }
        },
        async deleteMailing() {
            if (!this.templateSlugValue) {
                return;
            }
            if (!window.confirm('¿Eliminar este mailing?')) {
                return;
            }
            const response = await fetch(this.apiDeleteUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken(),
                    'X-APP-TOKEN': this.apiToken,
                },
                body: JSON.stringify({
                    slug: this.mailingFileName || this.templateSlugValue,
                }),
            });
            if (!response.ok) {
                alert('No se pudo eliminar el mailing.');
                return;
            }
            window.location.href = this.indexUrl;
        },
        setEditorMode(mode) {
            this.editorMode = mode;
            if (this.historyIndex === -1 && this.htmlDraft) {
                this.resetHistory(this.htmlDraft);
            }
            if (mode === 'visual') {
                this.syncBlocksFromHtml(true);
            }
        },
        showToast(message) {
            if (window.$ && typeof window.$.notify === 'function') {
                window.$.notify({ message }, { type: 'success' });
                return;
            }
            alert(message);
        },
        currentHtml() {
            if (this.editorMode === 'html') {
                return this.stripMailingHeader(this.htmlDraft || '');
            }
            if (!this.htmlDraft) {
                return '';
            }
            return this.htmlDraft;
        },
        buildSnapshot() {
            return {
                name: (this.mailingName || '').trim(),
                subject: (this.mailingSubject || '').trim(),
                html: this.currentHtml() || '',
                attachments: this.mailingAttachments || [],
            };
        },
        refreshSnapshot() {
            this.lastSavedSnapshot = this.buildSnapshot();
            this.isDirty = false;
        },
        updateDirtyState() {
            if (!this.lastSavedSnapshot) {
                return;
            }
            const current = JSON.stringify(this.buildSnapshot());
            const saved = JSON.stringify(this.lastSavedSnapshot);
            this.isDirty = current !== saved;
        },
        handleBeforeUnload(event) {
            if (!this.isDirty) {
                return;
            }
            event.preventDefault();
            event.returnValue = '';
        },
        pushHistory(html) {
            if (this.historyLocked) {
                return;
            }
            if (!html) {
                return;
            }
            if (this.historyIndex >= 0 && this.history[this.historyIndex] === html) {
                return;
            }
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }
            this.history.push(html);
            this.historyIndex = this.history.length - 1;
            if (this.history.length > 3) {
                this.history.shift();
                this.historyIndex = this.history.length - 1;
            }
        },
        resetHistory(html) {
            this.historyLocked = true;
            this.history = [];
            this.historyIndex = -1;
            this.historyLocked = false;
            this.pushHistory(html);
        },
        stripMailingHeader(html) {
            if (!html) return '';
            const re = new RegExp('<!--\\s*mailing:name=\"[^\"]*\";created_at=\"[^\"]*\"\\s*-->\\s*', 'i');
            return html.replace(re, '');
        },
        extractContenidoFromHtml(html) {
            if (!html) {
                return '';
            }
            try {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const contenido = doc.getElementById('contenido');
                if (!contenido) {
                    return html;
                }
                const raw = contenido.innerHTML.trim();
                const emptyParagraphs = new RegExp('(<p>(?:\\s|&nbsp;|<br\\s*\\/?\\s*>)*<\\/p>\\s*)+', 'gi');
                return raw.replace(emptyParagraphs, '');
            } catch (e) {
                return html;
            }
        },
    },
};
</script>

<style scoped>
.mailing-builder {
    background: #ffffff;
    border: 1px solid #e1e1e1;
    border-radius: 8px;
    padding: 16px;
}

.builder-sidebar {
    border: 1px solid #eef1f5;
    border-radius: 6px;
    padding: 12px;
    background: #f9fafb;
}

.builder-canvas {
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    padding: 12px;
    min-height: 300px;
}
.canvas-toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
}
.canvas-toolbar .btn.active {
    background: #0d6efd;
    color: #ffffff;
}
.canvas-preview iframe {
    width: 100%;
    min-height: 320px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
}
.canvas-editor textarea {
    width: 100%;
}

.canvas-blocks {
    display: grid;
    gap: 12px;
    margin-top: 12px;
}

.canvas-block {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: #ffffff;
}

.canvas-block-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 13px;
}

.canvas-block-body {
    font-size: 14px;
}
.visual-builder {
    display: grid;
    gap: 12px;
}
.visual-block {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: #ffffff;
    position: relative;
}
.drop-placeholder {
    border: 2px dashed #0d6efd;
    border-radius: 8px;
    height: 56px;
    background: rgba(13, 110, 253, 0.08);
    margin: 6px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.visual-block-toolbar {
    position: absolute;
    top: 6px;
    right: 6px;
    display: flex;
    gap: 6px;
    align-items: center;
    z-index: 5;
}
.visual-block-handle {
    cursor: grab;
    font-size: 12px;
    color: #6b7280;
    user-select: none;
}
.column-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 8px;
}
.column-cell {
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    padding: 6px;
    min-height: 60px;
    background: #f9fafb;
}
.column-drop {
    min-height: 48px;
}

.option-grid {
    display: grid;
    gap: 10px;
    margin-top: 12px;
}
.variable-list {
    display: grid;
    gap: 6px;
}
.variable-header {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-top: 8px;
}

.option-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s ease;
}

.option-card:hover {
    border-color: #94a3b8;
}

.option-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f3f4f6;
    color: #374151;
}

.builder-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.builder-icon i {
    font-size: 14px;
    line-height: 1;
}

.option-name {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}
.context-menu {
    position: fixed;
    z-index: 1000;
    min-width: 140px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
    padding: 6px;
}
.context-item {
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 6px 8px;
    font-size: 13px;
    color: #111827;
    border-radius: 4px;
}
.context-item:hover {
    background: #f3f4f6;
}
.modal-mask {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
}
.modal-panel {
    width: 100%;
    max-width: 520px;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
    overflow: hidden;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}
.modal-header,
.modal-footer {
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 auto;
}
.modal-footer {
    border-bottom: 0;
    border-top: 1px solid #e5e7eb;
    justify-content: flex-end;
    gap: 8px;
}
.modal-body {
    padding: 16px;
    overflow-y: auto;
    flex: 1 1 auto;
}
</style>
